import emojiRegex from 'emoji-regex';
import {
    IChatMessage,
    IPrompt,
    TypedChatMessage
} from '~/packages/ai-chat/types';
import { AIResponse, PriorityListData } from '~/packages/ai-core';
import {
    AIRequest,
    AIRequestContext,
    BusinessTrendsData,
    StructuredRequest,
    CallToActionData
} from '~/packages/ai-core/types/server-types';

export const SUPPORTED_CONVERSIONS_TO_MARKDOWN = ['priority_list'];

export const mapResponseToMessage = (response: AIResponse): IChatMessage => {
    let message: IChatMessage;

    switch (response.type) {
        case 'markdown':
            message = {
                type: response.type,
                role: 'assistant',
                content: response.content,
                is_feedbackable: response.is_feedbackable
            };
            break;
        case 'structured':
            message = {
                type: response.content.type,
                role: 'assistant',
                content: response.content.data
            } as TypedChatMessage<typeof response.content.type>;
            break;
        case 'action':
            // Action responses cannot be mapped to messages
            // This case is included for completeness and visibility onlu
            throw new Error(
                'Action responses should not be mapped to messages'
            );
        default:
            throw new Error(`Unsupported response type: ${response.type}`);
    }

    return {
        ...message,
        id: response.object_id,
        created_at: response.object_created_at
            ? new Date(response.object_created_at)
            : undefined
    };
};

export const splitLeadingEmoji = (
    str: string
): { emoji: string | null; text: string } => {
    const regex = emojiRegex();
    const match = str.match(regex);

    if (match && str.startsWith(match[0])) {
        const emoji = match[0];
        const text = str.slice(emoji.length).trimStart();
        return { emoji, text };
    }

    return { emoji: null, text: str.trimStart() };
};

// ----- Monday Priorities HTML Conversion Utilities ----- //

const wrapContentWithHtmlTag = (
    content: string,
    tag: string,
    args?: Record<string, string>
) => {
    const attrs = args
        ? Object.entries(args)
              .map(([key, value]) => `${key}="${value}"`)
              .join(' ')
        : '';

    return `<${tag} ${attrs}>${content}</${tag}>`;
};

export const convertPriorityListMessageToHtml = (
    content: PriorityListData
): string => {
    let html = '';
    // Main title
    if (content.title) {
        html += wrapContentWithHtmlTag(content.title, 'p');
    }
    // Sections
    for (const section of content.sections) {
        if (section.title) {
            html += wrapContentWithHtmlTag(section.title, 'h2');
        }
        if (Array.isArray(section.items)) {
            for (const item of section.items) {
                if (item.type === 'action_item') {
                    html += wrapContentWithHtmlTag(item.content.text, 'p');
                    const { action } = item.content;
                    if (action && action.args) {
                        html += wrapContentWithHtmlTag('', 'hbaction', {
                            type: action.type,
                            ...action.args
                        });
                    } else if (action) {
                        html += wrapContentWithHtmlTag('', 'hbaction', {
                            type: action.type
                        });
                    }
                }
            }
        }
    }
    return html;
};

export const convertBusinessTrendsMessageToHtml = (
    content: BusinessTrendsData
): string => {
    let html = '';
    html += `# ${content.title}\n\n`;
    html += `${content.intro}\n\n`;

    if (content.stats) {
        for (const stat of content.stats) {
            html += `### ${stat.title}\n\n`;
            if (stat.subtitle) {
                html += `**${stat.subtitle}**\n\n`;
            }
            if (stat.description) {
                html += `${stat.description}\n\n`;
            }
        }
    }

    if (content.trends_section_intro) {
        html += '---\n\n';
        html += `# ${content.trends_section_intro}\n\n`;
    }

    // Sections
    for (const section of content.sections) {
        if (section.items.length) {
            html += `## ${section.title}\n\n`;
            for (const item of section.items) {
                html += `**${item.title}**\n\n`;
                html += `${item.description}\n\n`;
            }
            html += '---\n\n';
        }
    }

    if (content.outro) {
        html += `${content.outro}\n\n`;
    }

    return html;
};

export const convertCallToActionToHtml = (
    content: CallToActionData
): string => {
    let html = '';

    const { type, text, args } = content ?? {};
    if (type) {
        const attributes: Record<string, string> = {
            type
        };

        if (text) {
            attributes.text = text;
        }

        if (args) {
            Object.assign(attributes, args);
        }

        html += wrapContentWithHtmlTag('', 'hbaction', attributes);
    }

    return html;
};

export const getAIRequest = (
    prompt: IPrompt,
    context: AIRequestContext
): AIRequest => {
    if (prompt.type === 'markdown') {
        return {
            ...prompt,
            context
        };
    } else {
        return {
            type: 'structured',
            context,
            content: {
                type: prompt.type,
                data: prompt.content
            } as StructuredRequest
        };
    }
};
